import {
  CASE_TIME_FORMAT,
  getFormattedDateWithTz,
  InsighttDateTime,
} from '@rapid-recovery-agency-inc/insightt-shared';
import moment from 'moment-timezone';

import { store } from '../../redux/store';
import { durationBetweenTwoDates } from '../../helpers/utility';
import {
  CaseAssignee,
  MissedRepossession,
  RdnCase,
  SpottedVehicleReport,
} from '@rapid-recovery-agency-inc/insightt-graphql';

interface NormalizeMissedRepossessionsInput {
  __typename?: 'MissedRepossession' | undefined;
  createdAt: string;
  id: number;
  case: {
    __typename?: 'RDNCase' | undefined;
    spottedDate?: string;
  };
}

export const normalizeMissedRepossessions = (data?: NormalizeMissedRepossessionsInput[]) => {
  if (!data) return [];

  return data.map((item) => {
    const timezone = store.getState().auth?.timezone;
    const spottedDate = item.case.spottedDate;

    return {
      id: item.id,
      missedDate: getFormattedDateWithTz(item.createdAt, timezone),
      missedHour: getFormattedDateWithTz(item.createdAt, timezone, CASE_TIME_FORMAT),
      spottedDate: spottedDate ? getFormattedDateWithTz(spottedDate, timezone) : 'N/A',
      spottedHour: spottedDate ? getFormattedDateWithTz(spottedDate, timezone, CASE_TIME_FORMAT) : 'N/A',
      missedRepoTime: spottedDate ? durationBetweenTwoDates(spottedDate, item.createdAt) : 'N/A',
      missedRepoDate: spottedDate ? moment(item.createdAt).tz(timezone).diff(spottedDate) : null,
    };
  });
};

interface MapMissedRepoDataInput {
  rdnCases: Pick<RdnCase, 'missedRepossession' | 'spottedVehicleReports' | 'caseId'>[];
}

export const mapMissedRepoData = ({ rdnCases }: MapMissedRepoDataInput) => {
  const mappedData = [];

  for (const rdnCase of rdnCases) {
    const { missedRepossession, spottedVehicleReports } = rdnCase;
    for (const missedRepo of missedRepossession) {
      const filteredSpottedReport = spottedVehicleReports.filter((spottedReport) => {
        const spottedDate = InsighttDateTime.fromISO(spottedReport.spottedDate).miliseconds;
        const missedDate = InsighttDateTime.fromISO(missedRepo.createdAt).miliseconds;
        return spottedDate < missedDate;
      });

      const sortedSpottedReport = filteredSpottedReport.sort(
        (a, b) =>
          InsighttDateTime.fromISO(b.spottedDate).miliseconds -
          InsighttDateTime.fromISO(a.spottedDate).miliseconds,
      );

      mappedData.push({
        missedRepossession: missedRepo,
        spottedVehicleReport: sortedSpottedReport[0],
        caseId: rdnCase.caseId,
      });
    }
  }

  return mappedData;
};

interface MissedRepossessionData {
  missedRepossession: MissedRepossession;
  spottedVehicleReport: SpottedVehicleReport;
  caseId: number;
}
interface SortMissedRepossessionDataInput {
  data: MissedRepossessionData[];
  activeSortKey: 'rdnCaseId' | 'spottedDate' | 'missedDate' | 'spottedBy' | 'reachStatus';
  sortOrder: 'asc' | 'desc';
}

export const sortMissedRepossessionData = ({
  data,
  activeSortKey,
  sortOrder,
}: SortMissedRepossessionDataInput) => {
  return data.sort((a, b) => {
    const aSortValue = getSortValue(a, activeSortKey);
    const bSortValue = getSortValue(b, activeSortKey);

    if (sortOrder === 'asc') {
      return aSortValue.localeCompare(bSortValue);
    } else {
      return bSortValue.localeCompare(aSortValue);
    }
  });

  function getSortValue(data: MissedRepossessionData, sortKey: string): string {
    switch (sortKey) {
      case 'rdnCaseId':
        return String(data.caseId);
      case 'spottedDate':
        return data.spottedVehicleReport.spottedDate;
      case 'missedDate':
        return data.missedRepossession.createdAt;
      case 'spottedBy':
        return data.spottedVehicleReport.user.firstName || '';
      case 'reachStatus':
        return data.spottedVehicleReport.unitReachStatus || '';
      default:
        return '';
    }
  }
};

export function formatUsersForRHLDetailsShape(assignedUsers?: Partial<CaseAssignee>[]) {
  if (!assignedUsers) return [];

  return assignedUsers.map((c) => {
    const assignedByUserId = c.assignedBy?.id as number;
    const assignedToId = c.user?.id as number;

    return {
      ...c,
      assignedBy: {
        __ref: `User:${assignedByUserId}`,
      },
      user: {
        __ref: `User:${assignedToId}`,
      },
    };
  });
}
